import { useContext, useState } from 'react';
import { PlaycastContext } from '../PlaycastWrapper';
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@playcastdotio/Shadcn';

export const ShadowDisplayControl = () => {
  const context = useContext(PlaycastContext);

  // Display configuration state
  const [width, setWidth] = useState(1920);
  const [height, setHeight] = useState(1080);
  const [refreshRate, setRefreshRate] = useState(60);
  const [deviceName, setDeviceName] = useState('Playcast Display');
  const [serialNumber, setSerialNumber] = useState('PC001');
  const [displayCount, setDisplayCount] = useState(1);
  const [removeGuid, setRemoveGuid] = useState('');

  // Resolution presets
  const resolutionPresets = [
    { name: '1920x1080 (1080p)', width: 1920, height: 1080 },
    { name: '2560x1440 (1440p)', width: 2560, height: 1440 },
    { name: '3840x2160 (4K)', width: 3840, height: 2160 },
    { name: '1280x720 (720p)', width: 1280, height: 720 },
    { name: '1600x900', width: 1600, height: 900 },
    { name: '1366x768', width: 1366, height: 768 },
  ];

  const refreshRateOptions = [30, 60, 75, 120, 144];

  const handlePresetChange = (value: string) => {
    const preset = resolutionPresets.find((p) => p.name === value);
    if (preset) {
      setWidth(preset.width);
      setHeight(preset.height);
    }
  };

  const handleAddDisplay = () => {
    context.targetFunctions.shadowDisplayAddDisplay(
      width,
      height,
      refreshRate,
      deviceName,
      serialNumber
    );
  };

  const handleAddMultipleDisplays = () => {
    for (let i = 0; i < displayCount; i++) {
      context.targetFunctions.shadowDisplayAddDisplay(
        width,
        height,
        refreshRate,
        `${deviceName} ${i + 1}`,
        `${serialNumber}${i + 1}`
      );
    }
  };

  const handleRemoveAllDisplays = () => {
    context.targetFunctions.shadowDisplayRemoveAllDisplays();
  };

  const handleGetDisplays = () => {
    context.targetFunctions.shadowDisplayGetDisplays();
  };

  const handleGetDriverStatus = () => {
    context.targetFunctions.shadowDisplayGetDriverStatus();
  };

  const handlePingDriver = () => {
    context.targetFunctions.shadowDisplayPingDriver();
  };

  const handleGetProtocolVersion = () => {
    context.targetFunctions.shadowDisplayGetProtocolVersion();
  };

  const handleGetWatchdog = () => {
    context.targetFunctions.shadowDisplayGetWatchdog();
  };

  const handleRemoveByGuid = () => {
    const guid = removeGuid.trim();
    if (!guid) return;
    context.targetFunctions.shadowDisplayRemoveDisplay(guid);
  };

  const PREVIEW_CAMERAS = [
    'ShadowCam00',
    'ShadowCam01',
    'ShadowCam02',
    'ShadowCam03',
    'ShadowCam04',
    'ShadowCam05',
    'ShadowCam06',
    'ShadowCam07',
    'ShadowCam08',
  ];

  const handleToggleVideoPreview = () => {
    context.targetFunctions.showVideoPreview();
  };

  const handleSelectPreviewCamera = (camera: string) => {
    context.electronFunctions?.switchVideoPreviewCamera(camera);
  };

  return (
    <div className='flex flex-col space-y-4 rounded-lg border border-gray-700 p-4'>
      <h3 className='text-lg font-semibold'>ShadowDisplay Virtual Display Driver</h3>

      {/* Video Preview / ShadowCam Controls */}
      <div className='flex flex-row space-x-2'>
        <Button className='m-0' onClick={handleToggleVideoPreview}>
          Toggle Preview
        </Button>
        <Select value={context.stateStores.hostRuleState.videoPreviewCamera} onValueChange={handleSelectPreviewCamera}>
          <SelectTrigger>
            <SelectValue placeholder='Select a preview camera' />
          </SelectTrigger>
          <SelectContent className='z-[10000]'>
            {PREVIEW_CAMERAS.map((camera) => (
              <SelectItem key={camera} value={camera}>
                {camera}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Resolution Preset */}
      <div className='flex flex-col space-y-2'>
        <label className='text-sm font-medium'>Resolution Preset</label>
        <Select onValueChange={handlePresetChange}>
          <SelectTrigger>
            <SelectValue placeholder='Select a preset' />
          </SelectTrigger>
          <SelectContent>
            {resolutionPresets.map((preset) => (
              <SelectItem key={preset.name} value={preset.name}>
                {preset.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Custom Resolution */}
      <div className='grid grid-cols-2 gap-4'>
        <div className='flex flex-col space-y-2'>
          <label className='text-sm font-medium'>Width</label>
          <Input
            type='number'
            value={width}
            onChange={(e) => setWidth(parseInt(e.target.value) || 1920)}
            min={640}
            max={7680}
          />
        </div>
        <div className='flex flex-col space-y-2'>
          <label className='text-sm font-medium'>Height</label>
          <Input
            type='number'
            value={height}
            onChange={(e) => setHeight(parseInt(e.target.value) || 1080)}
            min={480}
            max={4320}
          />
        </div>
      </div>

      {/* Refresh Rate */}
      <div className='flex flex-col space-y-2'>
        <label className='text-sm font-medium'>Refresh Rate (Hz)</label>
        <Select value={refreshRate.toString()} onValueChange={(v) => setRefreshRate(parseInt(v))}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {refreshRateOptions.map((rate) => (
              <SelectItem key={rate} value={rate.toString()}>
                {rate} Hz
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Device Name and Serial */}
      <div className='grid grid-cols-2 gap-4'>
        <div className='flex flex-col space-y-2'>
          <label className='text-sm font-medium'>Device Name</label>
          <Input
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            maxLength={13}
          />
        </div>
        <div className='flex flex-col space-y-2'>
          <label className='text-sm font-medium'>Serial Number</label>
          <Input
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            maxLength={13}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className='flex flex-col space-y-2'>
        <div className='flex space-x-2'>
          <Button className='flex-1' onClick={handleAddDisplay}>
            Add Display
          </Button>
          <Button className='flex-1 bg-blue-600 hover:bg-blue-700' onClick={handleGetDisplays}>
            Get Displays
          </Button>
        </div>

        {/* Multi-display creation */}
        <div className='flex space-x-2'>
          <Input
            type='number'
            value={displayCount}
            onChange={(e) => setDisplayCount(parseInt(e.target.value) || 1)}
            min={1}
            max={10}
            className='w-20'
          />
          <Button className='flex-1' onClick={handleAddMultipleDisplays}>
            Add {displayCount} Display{displayCount > 1 ? 's' : ''}
          </Button>
        </div>

        <div className='flex space-x-2'>
          <Button className='flex-1 bg-red-600 hover:bg-red-700' onClick={handleRemoveAllDisplays}>
            Remove All Displays
          </Button>
          <Button className='flex-1 bg-yellow-600 hover:bg-yellow-700' onClick={handlePingDriver}>
            Ping Driver
          </Button>
        </div>

        <div className='flex flex-wrap gap-2'>
          <Button onClick={handleGetDriverStatus}>Get Driver Status</Button>
          <Button className='bg-slate-700 hover:bg-slate-600' onClick={handleGetProtocolVersion}>
            Get Protocol Version
          </Button>
          <Button className='bg-slate-700 hover:bg-slate-600' onClick={handleGetWatchdog}>
            Get Watchdog
          </Button>
        </div>

        {/* Remove by GUID */}
        <div className='flex flex-col space-y-2 rounded border border-gray-800 bg-gray-950/30 p-3'>
          <div className='text-sm font-medium'>Remove Display by GUID</div>
          <div className='flex flex-col gap-2 sm:flex-row'>
            <Input
              value={removeGuid}
              onChange={(e) => setRemoveGuid(e.target.value)}
              placeholder='e.g. {7B8A3C91-4F2E-4D5A-9E7F-2B1C8A9D3E6F}'
            />
            <Button className='bg-red-700 hover:bg-red-600' onClick={handleRemoveByGuid}>
              Remove
            </Button>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className='rounded bg-gray-800 p-3 text-xs text-gray-300'>
        <div className='mb-1 font-semibold'>Notes:</div>
        <ul className='list-inside list-disc space-y-1'>
          <li>Virtual displays appear as additional monitors in Windows</li>
          <li>Driver must be installed and running (requires admin)</li>
          <li>Ping driver periodically to prevent watchdog timeout</li>
          <li>Protocol/watchdog calls are best inspected via console logs / PlayJector logs</li>
          <li>Check console logs for responses from driver</li>
        </ul>
      </div>
    </div>
  );
};

